import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  FileSpreadsheet, 
  MoreHorizontal, 
  RefreshCw, 
  Trash2, 
  Calendar,
  User,
  FileText,
  Upload
} from "lucide-react";

const Uploads = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reimportLoading, setReimportLoading] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchUploads();
  }, []);

  const fetchUploads = async () => {
    try {
      const response = await axios.get(`${API}/uploads`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUploads(response.data);
    } catch (error) {
      console.error("Failed to fetch uploads:", error);
      toast.error("Failed to load uploads");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("token");
    navigate("/");
  };

  const handleReimport = async (uploadId, filename) => {
    setReimportLoading(uploadId);
    try {
      const response = await axios.post(`${API}/uploads/${uploadId}/reimport`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      // Build success message
      let message = `Re-imported ${response.data.imported} leads from ${filename}`;
      if (response.data.skipped_duplicates > 0) {
        message += `, skipped ${response.data.skipped_duplicates} duplicates`;
      }
      toast.success(message);
      
      // Show duplicate names if any
      if (response.data.duplicate_names && response.data.duplicate_names.length > 0) {
        const names = response.data.duplicate_names.slice(0, 5).join(", ");
        const more = response.data.skipped_duplicates > 5 ? ` and ${response.data.skipped_duplicates - 5} more` : "";
        toast.info(`Skipped: ${names}${more}`, { duration: 6000 });
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      fetchUploads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to re-import file");
    } finally {
      setReimportLoading(null);
    }
  };

  const handleDelete = async (uploadId) => {
    try {
      await axios.delete(`${API}/uploads/${uploadId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Upload deleted");
      setConfirmDelete(null);
      fetchUploads();
    } catch (error) {
      toast.error("Failed to delete upload");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="uploads-page">
      <Sidebar user={user} onLogout={handleLogout} />
      
      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 animate-fade-in">
          <div>
            <h1 
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Uploads
            </h1>
            <p className="text-slate-500 mt-1">
              Previously uploaded files - click to re-import
            </p>
          </div>
          <Button
            onClick={() => navigate('/leads')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload New File
          </Button>
        </div>

        {/* Uploads Table */}
        <Card className="border-slate-100 shadow-sm animate-fade-in">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Upload History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="spinner mx-auto"></div>
              </div>
            ) : uploads.length === 0 ? (
              <div className="p-12 text-center">
                <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-2">No files uploaded yet</p>
                <p className="text-sm text-slate-400">
                  Upload a CSV or Excel file from the Leads page to see it here
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">File</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Uploaded</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Uploaded By</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Leads Imported</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Re-import</TableHead>
                    <TableHead className="w-32"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {uploads.map((upload) => (
                    <TableRow 
                      key={upload.upload_id} 
                      className="table-row-hover"
                      data-testid={`upload-row-${upload.upload_id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            upload.file_type === 'csv' 
                              ? 'bg-emerald-100' 
                              : 'bg-blue-100'
                          }`}>
                            <FileSpreadsheet className={`w-5 h-5 ${
                              upload.file_type === 'csv' 
                                ? 'text-emerald-600' 
                                : 'text-blue-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{upload.filename}</p>
                            <p className="text-xs text-slate-500">
                              {formatFileSize(upload.file_size)} • {upload.file_type.toUpperCase()}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{formatDate(upload.uploaded_at)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-slate-600">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{upload.uploaded_by_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-indigo-100 text-indigo-700">
                          {upload.leads_imported} leads
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500">
                          {upload.last_imported_at !== upload.uploaded_at 
                            ? formatDate(upload.last_imported_at)
                            : '—'
                          }
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReimport(upload.upload_id, upload.filename)}
                            disabled={reimportLoading === upload.upload_id}
                            className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                            data-testid={`reimport-${upload.upload_id}`}
                          >
                            {reimportLoading === upload.upload_id ? (
                              <div className="spinner w-4 h-4"></div>
                            ) : (
                              <>
                                <RefreshCw className="w-4 h-4 mr-1" />
                                Re-import
                              </>
                            )}
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                onClick={() => setConfirmDelete(upload)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Delete Upload?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Are you sure you want to delete <strong>{confirmDelete?.filename}</strong>?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              This will only remove the file from your upload history. 
              Previously imported leads will not be affected.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleDelete(confirmDelete?.upload_id)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Uploads;
